import React, { useEffect, useState } from "react";
import { Filter } from "lucide-react";
import {
  syncNow,
  viewConnection,
  deleteConnectedSource,
} from "../../../services/AdminPortalAPIs";
import { message, Modal, Button } from "antd";
import LoadingSpinner from "src/components/reusable/LoadingSpinner";
import { AxiosError } from "axios";
import ReusableModal from "src/components/reusable/ConfirmationModal";
import StyledTable from "src/components/reusable/StyledTable";
import { getDataSourceColumns, type DataSource } from "./DataSourceColumns";
import { fetchConnectedSources } from "./DataSourceHandlers";
import ShopifyAuthForm from "./SourceDetailsPopUp";
import StyledDropdown from "src/components/reusable/StyledDropdown";
import StyledInput from "src/components/reusable/StyledInput";

type ConnectionDetails = {
  shopify_api_key?: string;
  shopify_hostname?: string;
  status?: string;
  api_key?: string;
  token?: string;
  secret?: string;
  [key: string]: string | number | boolean | null | undefined;
};

export default function DataSourceMonitoring() {
  const [allSources, setAllSources] = useState<DataSource[]>([]);
  const [actionInProgressId, setActionInProgressId] = useState<string | null>(
    null
  );
  const [filteredSources, setFilteredSources] = useState<DataSource[]>([]);
  const [search, setSearch] = useState("");
  const didFetchRef = React.useRef(false);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedDeleteItem, setSelectedDeleteItem] = useState<DataSource | null>(
    null
  );
  const [connectionDetails, setConnectionDetails] =
    useState<ConnectionDetails | null>(null);
  const [viewModalVisible, setViewModalVisible] = useState(false);
const isFilterActive = !!search || !!statusFilter || !!typeFilter;
  const fetchData = () =>
    fetchConnectedSources(setAllSources, setFilteredSources, setLoading);

  useEffect(() => {
    if (didFetchRef.current) {return;}
    didFetchRef.current = true;
    fetchData();
  }, []);

  useEffect(() => {
    let data = [...allSources];
    if (search) {
      const searchLower = search.toLowerCase();
      data = data.filter(
        item =>
          item.tenant_name.toLowerCase().includes(searchLower) ||
          item.tenant_email.toLowerCase().includes(searchLower) ||
          item.connected_source.toLowerCase().includes(searchLower)
      );
    }
    if (statusFilter)
      {data = data.filter(item => item.status === statusFilter);}
    if (typeFilter) {data = data.filter(item => item.type === typeFilter);}
    setFilteredSources(data);
  }, [search, statusFilter, typeFilter, allSources]);

  const handleSyncNow = async (
    user_email: string,
    source_name: string,
    tenant_name: string
  ) => {
    const actionId = `${user_email}-${source_name}`;
    setActionInProgressId(actionId);
    try {
      const res = await syncNow({ user_email, source_name });
      const sourceLabel = `${source_name} (${tenant_name})`;
      if (res.data?.success?.length > 0)
        {message.success(`Manual sync initiated for ${sourceLabel}`);}
      else if (res.data?.errors?.length > 0)
        {message.error(`Some pipelines failed to sync for ${sourceLabel}`);}
      else {message.info(`Sync completed for ${sourceLabel}`);}
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      const errorMessage = axiosError.response?.data?.message || "Sync failed";
      message.error(errorMessage);
    } finally {
      setActionInProgressId(null);
      fetchData();
    }
  };

  const handleViewConnection = async (
    user_email: string,
    source_name: string
  ) => {
    const actionId = `${user_email}-${source_name}`;
    setActionInProgressId(actionId);
    try {
      const res = await viewConnection({ user_email, source_name });
      if (res.data?.status) {
        setConnectionDetails(res.data);
        setViewModalVisible(true);
      } else {
        message.error("Failed to fetch connection details");
      }
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      const errorMessage =
        axiosError.response?.data?.message || "Failed to fetch connection";
      message.error(errorMessage);
    } finally {
      setActionInProgressId(null);
    }
  };

  const resetFilters = () => {
    setSearch("");
    setStatusFilter(null);
    setTypeFilter(null);
  };

  const handleDelete = async () => {
    if (!selectedDeleteItem) {return;}
    const actionId = `${selectedDeleteItem.tenant_email}-${selectedDeleteItem.connected_source}`;
    setActionInProgressId(actionId);
    const payload = {
      user_email: selectedDeleteItem.tenant_email,
      source_name: selectedDeleteItem.connected_source.toLowerCase(),
    };
    try {
      const res = await deleteConnectedSource(payload);
      if (res.data?.status) {
        message.success("Source deleted successfully.");
        fetchData();
      } else {
        message.error(res.data?.message || "Failed to delete source.");
      }
    } catch {
      message.error("Error deleting source. Please try again.");
    } finally {
      setShowDeleteModal(false);
      setSelectedDeleteItem(null);
      setActionInProgressId(null);
    }
  };

  const uniqueStatuses = Array.from(new Set(allSources.map(s => s.status)));
  const uniqueTypes = Array.from(new Set(allSources.map(s => s.type)));

  const columns = getDataSourceColumns({
    actionInProgressId,
    handleSyncNow,
    handleViewConnection,
    setSelectedDeleteItem,
    setShowDeleteModal,
  });

  return (
    <div className="admin-portal-page">
      <span className="heading-primary">Data Source Monitoring</span>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          <div className="filters">
            <StyledInput
              placeholder="Search sources or users..."
              value={search}
              aria-label="Search sources or users"
              onChange={e => setSearch(e.target.value)}
              className="admin-search"
            />
<StyledDropdown
  value={statusFilter ?? ""}
  onChange={setStatusFilter}
  style={{ width: 160 }}
  placeholder="All Status"
  popupClassName="custom-dropdown-menu"
  options={[
    { value: "", label: "All Status" },
    ...uniqueStatuses.map(status => ({ value: status, label: status }))
  ]}
/>

<StyledDropdown
  value={typeFilter ?? ""}
  onChange={setTypeFilter}
  style={{ width: 160 }}
  placeholder="All Types"
  popupClassName="custom-dropdown-menu"
  options={[
    { value: "", label: "All Types" },
    ...uniqueTypes.map(type => ({ value: type, label: type }))
  ]}
/>

            <Button
  icon={<Filter />}
  aria-label="Reset Filters"
  onClick={resetFilters}
  disabled={!isFilterActive}
>
  Reset
</Button>
          </div>
{filteredSources.length === 0 ? (
  <div style={{ textAlign: "center", margin: "40px 0", color: "#888" }}>
    <p>No data sources found.</p>
  </div>
) : (
  <StyledTable
    columns={columns}
    dataSource={filteredSources}
    pagination={{
      pageSize: 5,
      showSizeChanger: false,
    }}
    className="pipeline-table"
    rowClassName={() => "textCaption"}
  />
)}
        </>
      )}

      <Modal
        title="Connection Details"
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={null}
      >
        {connectionDetails ? (
          <div className="connection-details">
            {Object.entries(connectionDetails)
              .filter(
                ([key]) =>
                  !["status", "api_key", "token", "secret"].includes(key)
              )
              .map(([key, value]) => (
                <div key={key}>
                  <strong>
                    {key
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, c => c.toUpperCase())}
                    :
                  </strong>{" "}
                  {value}
                </div>
              ))}
          </div>
        ) : (
          <p>No details available.</p>
        )}
      </Modal>

      <ReusableModal
        modalOpen={showDeleteModal}
        onCancel={() => {
          setShowDeleteModal(false);
          setSelectedDeleteItem(null);
        }}
        onConfirm={handleDelete}
        title="Are you sure you want to delete this connection?"
        description={`This will delete the source "${selectedDeleteItem?.connected_source}" for "${selectedDeleteItem?.tenant_name}". Do you want to continue?`}
        icon="/Icons/data-src-del.svg"
        confirmText="Delete"
        cancelText="Cancel"
      />

      <Modal
        title="Connection Details"
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={null}
      >
        {connectionDetails?.shopify_api_key &&
        connectionDetails?.shopify_hostname ? (
          <ShopifyAuthForm
            shopify_api_key={connectionDetails.shopify_api_key}
            shopify_hostname={connectionDetails.shopify_hostname}
          />
        ) : (
          <p>No Shopify connection details available.</p>
        )}
      </Modal>
    </div>
  );
}
